/**
 * The stat-card row above the Call Off Management list — one card per
 * call-off status plus the derived shipment-stage cards. Clicking a card
 * drills the list into it.
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. Presentational: the filter state stays with the list, and a
 * click is reported through `onCardClick`.
 *
 * @link CallOffManagementTab for the consumer.
 */
import { useMemo } from 'react';
import type { CallOffStats } from '../../../lib/api';
import { Card, CardContent } from '../../ui/card';
import { Truck } from 'lucide-react';
import { STATUS_CONFIG, STAT_CARDS, type StatCardDef } from './callOffListConfig';

/** CR-7732: states a haulier never sees a load in, so never counts. */
const HAULIER_HIDDEN_STATUS_CARDS = ['draft', 'pending'];

// CR-7781: one row of up to nine cards at desktop width, sized to however many
// are showing. Written out in full because Tailwind only ships class names it
// can find verbatim in the source.
const DESKTOP_COLUMNS: Record<number, string> = {
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
  7: 'xl:grid-cols-7',
  8: 'xl:grid-cols-8',
  9: 'xl:grid-cols-9',
};

interface CallOffStatCardsProps {
  stats: CallOffStats;
  /** The status card currently drilled into ('' = none). */
  statusFilter: string;
  /** CR-7732: hides the cards for states a haulier is never shown. */
  isHaulier?: boolean;
  /** The shipment-stage card currently drilled into ('' = none). */
  asnStageFilter: string;
  onCardClick: (card: StatCardDef, isActive: boolean) => void;
}

export default function CallOffStatCards({
  stats, statusFilter, asnStageFilter, onCardClick, isHaulier = false,
}: CallOffStatCardsProps) {
  // CR-7142: which cards to render. A `hideWhenZero` stage card is dropped
  // while its count is zero, so a tenant that never ships direct never sees a
  // filter that can only ever return nothing. It stays visible once selected —
  // otherwise drilling in would remove the card you clicked and strand the
  // filter with no way back to it.
  const visibleCards = useMemo(
    () =>
      STAT_CARDS.filter(({ key, statKey, hideWhenZero }) => {
        // CR-7732: a load is handed to its haulier once the supplier has
        // confirmed it, so Draft and Pending could only ever read 0 for one.
        if (isHaulier && HAULIER_HIDDEN_STATUS_CARDS.includes(key)) return false;
        if (!hideWhenZero) return true;
        if (asnStageFilter === key) return true;
        return (statKey ? stats[statKey] ?? 0 : 0) > 0;
      }),
    [stats, asnStageFilter, isHaulier],
  );

  return (
    <div
      className={`grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 ${DESKTOP_COLUMNS[visibleCards.length] ?? 'xl:grid-cols-9'}`}
    >
      {visibleCards.map((card) => {
        const { kind, key, label: stageLabel, statKey, title } = card;
        const isStage = kind === 'stage';
        const config = STATUS_CONFIG[key];
        const label = isStage ? (stageLabel ?? key) : config.label;
        const Icon = isStage ? Truck : config.icon;
        const count = isStage
          ? (statKey ? stats[statKey] ?? 0 : 0)
          : (stats[key as keyof CallOffStats] || 0);
        const isActive = isStage
          ? asnStageFilter === key
          : statusFilter === key && !asnStageFilter;
        return (
          <Card
            key={`${kind}-${key}`}
            className={`cursor-pointer hover:border-primary/50 ${isActive ? 'border-primary ring-1 ring-primary/30' : ''}`}
            title={title}
            onClick={() => onCardClick(card, isActive)}
          >
            {/* CR-7781: the label takes the card's full width and wraps, with
                the icon beside the number, so nine cards fit one desktop row
                without a label running into its icon. Two label lines are
                always reserved so every number sits on the same line. */}
            <CardContent className="p-3">
              <span className="block min-h-[2.5em] text-xs leading-tight text-muted-foreground">{label}</span>
              <div className="flex items-center justify-between mt-1">
                <p className="text-2xl font-bold">{count}</p>
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
